import React from "react";
import { AlertTitle, Box, Button } from "@mui/material";
import { useNavigate } from "react-router";
import type { ClientAccess } from "../../models/UserProfile";
import "./BannerStyleSheet.css";

type ClientLicenseHealthBannerProps = {
    activeClient?: ClientAccess | null;
};

type BannerModel = {
    severity: "info" | "warning" | "error";
    title: string;
    message: string;
    showAction: boolean;
    actionLabel: string;
};

function getPercent(used: number, max: number): number {
    if (!max || max <= 0) return 0;
    return Math.round((used / max) * 100);
}

function buildBannerModel(activeClient?: ClientAccess | null): BannerModel | null {
    const access = activeClient?.licenseAccess;
    if (!activeClient || !access) return null;

    const portfolioPercent = getPercent(access.portfoliosUsed, access.portfoliosMax);
    const artworkPercent = getPercent(access.artworksUsed, access.artworksMax);

    const isPortfolioLimitReached = access.portfoliosUsed >= access.portfoliosMax && access.portfoliosMax > 0;
    const isArtworkLimitReached = access.artworksUsed >= access.artworksMax && access.artworksMax > 0;

    const isPortfolioNearLimit = portfolioPercent >= 85;
    const isArtworkNearLimit = artworkPercent >= 85;

    const isPortfolioMonitor = portfolioPercent >= 70;
    const isArtworkMonitor = artworkPercent >= 70;

    if (access.licenseExpired) {
        return {
            severity: "error",
            title: "Subscription expired",
            message: activeClient.isTenantAdmin
                ? `The subscription for ${activeClient.clientName} has expired. You can still view data, but editing is disabled until renewal.`
                : `The subscription for ${activeClient.clientName} has expired. You can still view data, but editing is disabled. Please contact the subscription administrator.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Renew"
        };
    }

    // [Stripe] A failed payment matters more than usage warnings.
    if (access.paymentIssue) {
        return {
            severity: "error",
            title: "Payment failed",
            message: activeClient.isTenantAdmin
                ? `The last subscription payment for ${activeClient.clientName} didn't go through. Update the card to avoid losing access.`
                : `There's a billing issue with this subscription. Please contact the subscription administrator.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Update card"
        };
    }

    if (isArtworkLimitReached) {
        return {
            severity: "error",
            title: "Artwork limit reached",
            message: activeClient.isTenantAdmin
                ? `This subscription is using ${access.artworksUsed} of ${access.artworksMax} artworks. Upgrade to continue adding artworks.`
                : `This subscription is using ${access.artworksUsed} of ${access.artworksMax} artworks. Please contact the subscription administrator.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Upgrade"
        };
    }

    if (isPortfolioLimitReached) {
        return {
            severity: "error",
            title: "Portfolio limit reached",
            message: activeClient.isTenantAdmin
                ? `This subscription is using ${access.portfoliosUsed} of ${access.portfoliosMax} portfolios. Upgrade to continue adding portfolios.`
                : `This subscription is using ${access.portfoliosUsed} of ${access.portfoliosMax} portfolios. Please contact the subscription administrator.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Upgrade"
        };
    }

    // [Stripe] Subscriptions that renew themselves aren't "expiring" (monthly ones would warn every month).
    if (!access.autoRenews && access.daysRemaining > 0 && access.daysRemaining <= 14) {
        return {
            severity: "warning",
            title: access.daysRemaining === 1
                ? "Subscription expires tomorrow"
                : `Subscription expires in ${access.daysRemaining} days`,
            message: activeClient.isTenantAdmin
                ? `Renew soon to avoid interruption for ${activeClient.clientName}.`
                : `This subscription is nearing expiry. Please contact the subscription administrator if renewal is needed.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Renew"
        };
    }

    if (isArtworkNearLimit) {
        return {
            severity: "warning",
            title: "Artwork usage nearing limit",
            message: `This subscription is using ${access.artworksUsed} of ${access.artworksMax} artworks.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Upgrade"
        };
    }

    if (isPortfolioNearLimit) {
        return {
            severity: "warning",
            title: "Portfolio usage nearing limit",
            message: `This subscription is using ${access.portfoliosUsed} of ${access.portfoliosMax} portfolios.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Upgrade"
        };
    }

    if (isArtworkMonitor) {
        return {
            severity: "info",
            title: "Artwork usage increasing",
            message: `This subscription has used ${artworkPercent}% of its artwork allowance.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Review"
        };
    }

    if (isPortfolioMonitor) {
        return {
            severity: "info",
            title: "Portfolio usage increasing",
            message: `This subscription has used ${portfolioPercent}% of its portfolio allowance.`,
            showAction: activeClient.isTenantAdmin,
            actionLabel: "Review"
        };
    }

    return null;
}

const ClientLicenseHealthBanner: React.FC<ClientLicenseHealthBannerProps> = ({ activeClient }) => {
    const navigate = useNavigate();

    const banner = buildBannerModel(activeClient);
    if (!banner) return null;

    const handleAction = () => {
        navigate("/admin/tenant");
    };

    return (
        <Box className={`license-banner ${banner.severity}`}>
            <div className="license-banner-content">
                <div className="license-banner-text">
                    <AlertTitle sx={{ mb: 0.25 }}>{banner.title}</AlertTitle>
                    <span>{banner.message}</span>
                </div>

                {banner.showAction && (
                    <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        onClick={handleAction}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        {banner.actionLabel}
                    </Button>
                )}
            </div>
        </Box>
    );
};

export default ClientLicenseHealthBanner;